import { motion } from "framer-motion";
import { MapPin, Truck } from "lucide-react";

const CITIES = [
  "Valenciennes",
  "Anzin",
  "Saint-Saulve",
  "Marly",
  "Denain",
  "Bruay-sur-l'Escaut",
  "Condé-sur-l'Escaut",
  "Onnaing",
  "Vieux-Condé",
  "Raismes",
  "Aulnoy-lez-Valenciennes",
  "Famars",
  "Maing",
  "Trith-Saint-Léger",
  "Fresnes-sur-Escaut",
  "Beuvrages",
  "Hérin",
  "Quiévrechain",
  "Hautmont",
  "Maubeuge",
];

const SERVICES_SEO = [
  {
    title: "Ravalement de façade",
    text:
      "Spécialiste du ravalement de façade dans le Nord (59), nous redonnons à votre maison toute sa splendeur grâce à un enduit projeté gratté fin, taloché ou écrasé. Chaque chantier débute par un diagnostic précis du support et le choix de la finition la plus adaptée à votre architecture — typique du Valenciennois et du bassin minier.",
  },
  {
    title: "Rejointoiement de briques",
    text:
      "Les maisons en brique rouge du Nord sont notre signature. Nous remplaçons les joints abîmés par un mortier neuf à la teinte parfaitement assortie (chaux, ciment, mixte), redonnant 30 ans de vie à votre façade et renforçant son étanchéité face aux intempéries du Nord.",
  },
  {
    title: "Imitation pierre & décor",
    text:
      "Sculptée à la main : chaînes d'angle pierre de taille, contours de fenêtres, soubassements, bandeaux verticaux, imitation brique vieillie. Notre maîtrise des décors en pierre reconstituée transforme une façade ordinaire en propriété haut de gamme.",
  },
  {
    title: "Isolation thermique extérieure (ITE)",
    text:
      "Isoler par l'extérieur c'est gagner jusqu'à 25 % sur la facture de chauffage et 20 ans de jeunesse à la façade. Nous couplons l'ITE avec une finition enduit projeté ou imitation pierre, et vous accompagnons sur MaPrimeRénov' et les aides locales du département du Nord.",
  },
];

export default function ZonesIntervention() {
  return (
    <section
      id="zones"
      data-testid="zones-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#050505] to-[#080808]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center max-w-3xl mx-auto"
        >
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <Truck size={12} />
            <span>Nos prestations · Zones d&apos;intervention</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Votre spécialiste façade
            <br />
            <em className="text-[#FF5A00] not-italic font-normal">à Valenciennes & dans le Nord (59)</em>
          </h2>
          <p className="text-neutral-300 text-base md:text-lg leading-relaxed">
            Entreprise familiale implantée à Valenciennes depuis plus de 25 ans, GMC Rénovation Façade
            est <strong className="text-white">la référence du ravalement de façade haut de gamme</strong>{" "}
            dans le département du Nord. De l&apos;enduit projeté au rejointoiement de briques en passant
            par l&apos;imitation pierre de taille, nous maîtrisons l&apos;ensemble des techniques de
            rénovation extérieure.
          </p>
        </motion.div>

        {/* Services SEO */}
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          {SERVICES_SEO.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`zones-service-${i}`}
              className="glass rounded-2xl p-6 md:p-8 hover:border-white/20 transition-colors"
            >
              <h3 className="font-display text-2xl text-white mb-3">{s.title}</h3>
              <p className="text-neutral-400 leading-relaxed text-sm md:text-base">{s.text}</p>
            </motion.article>
          ))}
        </div>

        {/* Cities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass-strong rounded-3xl p-6 md:p-10"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] flex items-center justify-center">
              <MapPin size={16} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-white">
              Nous intervenons dans tout le Valenciennois & le Nord
            </h3>
          </div>
          <p className="text-neutral-400 text-sm md:text-base mb-6 max-w-3xl">
            Une flotte de 4 véhicules et une équipe locale pour des chantiers rapides et un suivi
            personnalisé partout dans le département du Nord (59). Voici les principales communes que nous
            couvrons régulièrement :
          </p>
          <ul
            data-testid="zones-cities"
            className="flex flex-wrap gap-2"
          >
            {CITIES.map((city) => (
              <li
                key={city}
                className="text-sm text-neutral-200 bg-white/[0.04] border border-white/10 px-3 py-1.5 rounded-full hover:border-[#FF5A00]/40 hover:text-white transition-colors"
              >
                {city}
              </li>
            ))}
            <li className="text-sm text-[#FF5A00] bg-[#FF5A00]/10 border border-[#FF5A00]/30 px-3 py-1.5 rounded-full">
              + tout le département 59
            </li>
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
